import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ThumbsUp, MessageSquare, Clock, Pencil, Trash2, Loader, Bell, Users, CheckCircle, LogIn, ArrowLeft } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useBookIdeaDetail } from '../hooks/useBookIdeaDetail';
import { useComments } from '../hooks/useComments';
import { usePolls } from '../hooks/usePolls';
import { useMilestones } from '../hooks/useMilestones';
import { useIdeaFollows } from '../hooks/useIdeaFollows';
import { useCharacters } from '../hooks/useCharacters';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import ActivityFeed from '../components/ActivityFeed';
import IdeaSidebar from '../components/IdeaSidebar';
import CreatePollModal from '../components/CreatePollModal';
import CreateMilestoneModal from '../components/CreateMilestoneModal';
import CreateCharacterModal from '../components/CreateCharacterModal';
import IdeaFollowButton from '../components/IdeaFollowButton';
import SlugRedirect from '../components/SlugRedirect';
import SocialShare from '../components/SocialShare';

function BetaReaderPrompt() {
  return (
    <div className="flex items-center justify-center p-8 bg-indigo-50 rounded-lg">
      <div className="text-center">
        <Users className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Want to be a Beta Reader?
        </h3>
        <p className="text-gray-600 mb-4">
          Sign in or create an account to apply as a beta reader for this book.
        </p>
        <Link
          to="/auth"
          className="inline-flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <LogIn className="w-5 h-5" />
          Sign In to Apply
        </Link>
      </div>
    </div>
  );
}

export default function BookIdea() {
  return (
    <SlugRedirect type="idea">
      <BookIdeaContent />
    </SlugRedirect>
  );
}

function BookIdeaContent({ id = '' }: { id?: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { idea, loading: ideaLoading, error: ideaError, toggleVote } = useBookIdeaDetail(id);
  const { comments, loading: commentsLoading, error: commentsError, addComment, updateComment, deleteComment, canDeleteComment } = useComments(id, idea?.author_id || '');
  const { polls, loading: pollsLoading, error: pollsError, createPoll, vote: votePoll, closePoll, deletePoll } = usePolls(id);
  const { milestones, loading: milestonesLoading, error: milestonesError, createMilestone, updateMilestone, deleteMilestone } = useMilestones(id);
  const { characters, loading: charactersLoading, error: charactersError, createCharacter, toggleFavorite: toggleCharacterFavorite } = useCharacters(id);
  const { isFollowing, followersCount, loading: followLoading, toggleFollow } = useIdeaFollows(id);

  const [isPollModalOpen, setIsPollModalOpen] = useState(false);
  const [isMilestoneModalOpen, setIsMilestoneModalOpen] = useState(false);
  const [isCharacterModalOpen, setIsCharacterModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [applicationError, setApplicationError] = useState<string | null>(null);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [hasExistingApplication, setHasExistingApplication] = useState(false);

  // Form state for beta reader application
  const [motivation, setMotivation] = useState('');
  const [completionWeeks, setCompletionWeeks] = useState(4);
  const [preferredGenres, setPreferredGenres] = useState<string[]>([]);
  const [critiqueAreas, setCritiqueAreas] = useState<string[]>([]);
  const [acceptsFollowup, setAcceptsFollowup] = useState(true);
  const [additionalInfo, setAdditionalInfo] = useState('');

  const isAuthor = user?.id === idea?.author_id;
  const isMainProfile = idea?.identity_id === idea?.author_id;

  // Check for existing application
  useEffect(() => {
    if (user && idea) {
      checkExistingApplication();
    }
  }, [user, idea]);

  const checkExistingApplication = async () => {
    try {
      const { data, error } = await supabase
        .from('beta_reader_applications')
        .select('id, status')
        .eq('idea_id', id)
        .eq('reader_id', user!.id)
        .maybeSingle();

      setHasExistingApplication(!!data);
    } catch (error) {
      console.error('Error checking existing application:', error);
      setHasExistingApplication(false);
    }
  };

  const handleApplyAsBetaReader = async () => {
    if (!user || !idea) return;

    try {
      setApplicationError(null);

      const { error } = await supabase
        .from('beta_reader_applications')
        .insert({
          idea_id: idea.id,
          reader_id: user.id,
          author_id: idea.author_id,
          motivation: motivation.trim(),
          completion_weeks: completionWeeks,
          preferred_genres: preferredGenres,
          critique_areas: critiqueAreas,
          accepts_followup: acceptsFollowup,
          additional_info: additionalInfo.trim() || null
        });

      if (error) throw error;

      setMotivation('');
      setCompletionWeeks(4);
      setPreferredGenres([]);
      setCritiqueAreas([]);
      setAcceptsFollowup(true);
      setAdditionalInfo('');
      setIsApplying(false);
      setApplicationSubmitted(true);
      setHasExistingApplication(true);

      setTimeout(() => {
        setApplicationSubmitted(false);
      }, 5000);
    } catch (err) {
      setApplicationError(err instanceof Error ? err.message : 'Failed to submit application');
    }
  };

  const handleDelete = async () => {
    if (!isAuthor || !window.confirm('Are you sure you want to delete this book idea? This action cannot be undone.')) {
      return;
    }

    try {
      setIsDeleting(true);
      const { error } = await supabase
        .from('book_ideas')
        .delete()
        .eq('id', id)
        .eq('author_id', user!.id);

      if (error) throw error;
      navigate(`/profile/${user!.id}`);
    } catch (err) {
      console.error('Error deleting book idea:', err);
      alert('Failed to delete book idea. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (ideaLoading || pollsLoading || milestonesLoading || charactersLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (ideaError || !idea) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
        {ideaError || 'Book idea not found'}
      </div>
    );
  }

  const shareUrl = `${window.location.origin}/idea/${idea.slug || id}`;
  const shareTitle = `Check out "${idea.title}" on StoryMuse`;
  const shareDescription = idea.description;

  return (
    <div className="max-w-7xl mx-auto px-4">
      {/* Back Link */}
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-accent1 hover:text-accent2 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Book Ideas
        </Link>
      </div>

      <article className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        {idea.cover_image && (
          <div className="relative h-64 md:h-96">
            <img
              src={idea.cover_image}
              alt={`Cover for ${idea.title}`}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center justify-between">
            <Link 
              to={isMainProfile ? `/profile/${idea.identity_id}` : `/pen-name/${idea.identity_id}`}
              className="flex items-center gap-2 hover:text-indigo-600"
            >
              {idea.identity_avatar_url ? (
                <img
                  src={idea.identity_avatar_url}
                  alt={idea.identity_username}
                  className="w-10 h-10 rounded-full"
                />
              ) : (
                <div className="w-10 h-10 bg-indigo-100 rounded-full" />
              )}
              <div>
                <span className="font-medium span-user">{idea.identity_username}</span>
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatDistanceToNow(new Date(idea.created_at), { addSuffix: true })}
                </div>
              </div>
            </Link>

            <div className="flex items-center gap-2">
                <button
                onClick={toggleVote}
                disabled={!user}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  idea.has_voted
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } ${!user && 'opacity-50 cursor-not-allowed'}`}
                title={user ? undefined : 'Please signup or login to vote'}
              >
                <ThumbsUp className="w-5 h-5" />
                <span>{idea.votes_count}</span>
              </button>
              {!isAuthor && !followLoading && (
                <IdeaFollowButton
                  isFollowing={isFollowing}
                  onToggle={toggleFollow}
                  className="mr-2"
                />
              )}
              {isAuthor ? (
                <>
                  <Link
                    to={`/idea/${id}/edit`}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </Link>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                  >
                    <Trash2 className="w-4 h-4" />
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                </>
              ) : (
                <SocialShare
                  url={shareUrl}
                  title={shareTitle}
                  description={shareDescription}
                />
              )}

            </div>
          </div>

          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{idea.title}</h1>
            <p className="text-gray-700 whitespace-pre-wrap">{idea.description}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            {[...idea.genre, ...(idea.custom_tags || [])].map((tag) => (
              <Link
                key={tag}
                to={`/?search=${encodeURIComponent(tag)}`}
                className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full hover:bg-indigo-100"
              >
                {tag}
              </Link>
            ))}
          </div>
        </div>
      </article>

      {/* Beta Reader Application Section */}
      {!isAuthor && idea.accepting_beta_readers && (
        <div className="mt-6 p-6 bg-violet-50 rounded-lg">
          {!user ? (
            <BetaReaderPrompt />
          ) : hasExistingApplication ? (
            <div className="flex items-center gap-3 text-violet-900">
              <CheckCircle className="w-6 h-6 text-violet-600" />
              <div>
                <h3 className="font-semibold">Application Submitted</h3>
                <p className="text-sm text-violet-700">
                  You've already applied to be a beta reader for this book. Check your dashboard for updates!
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-violet-900 flex items-center gap-2">
                    <Users className="w-5 h-5 text-violet-600" />
                    Beta Reader Applications Open
                  </h3>
                  <p className="text-violet-700 mt-1">
                    Help shape this story by becoming a beta reader!
                  </p>
                </div>
                {!isApplying ? (
                  <button
                    onClick={() => setIsApplying(true)}
                    className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
                  >
                    Apply Now
                  </button>
                ) : (
                  <button
                    onClick={() => setIsApplying(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                )}
              </div>

              {applicationSubmitted && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Application Submitted Successfully!</p>
                    <p className="text-sm">You'll be notified when the author reviews your application.</p>
                  </div>
                </div>
              )}

              {isApplying && (
                <div className="mt-4 space-y-6">
                  {applicationError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                      {applicationError}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-violet-900 mb-2">
                      Why do you want to be a beta reader for this book?
                    </label>
                    <textarea
                      value={motivation}
                      onChange={(e) => setMotivation(e.target.value)}
                      className="w-full p-3 border border-violet-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      rows={4}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-violet-900 mb-2">
                      How many weeks do you need to complete the beta reading?
                    </label>
                    <input
                      type="number"
                      value={completionWeeks}
                      onChange={(e) => setCompletionWeeks(parseInt(e.target.value))}
                      min={1}
                      max={12}
                      className="w-full p-3 border border-violet-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-violet-900 mb-2">
                      What genres do you usually read?
                    </label>
                    <div className="space-y-2">
                      {['Fantasy', 'Science Fiction', 'Romance', 'Mystery', 'Thriller', 'Horror', 'Literary Fiction'].map(genre => (
                        <label key={genre} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={preferredGenres.includes(genre)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPreferredGenres([...preferredGenres, genre]);
                              } else {
                                setPreferredGenres(preferredGenres.filter(g => g !== genre));
                              }
                            }}
                            className="rounded border-violet-300 text-violet-600 focus:ring-violet-500"
                          />
                          <span className="text-violet-900">{genre}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-violet-900 mb-2">
                      What aspects are you most comfortable critiquing?
                    </label>
                    <div className="space-y-2">
                      {['Plot', 'Character Development', 'Pacing', 'Dialogue', 'World-building', 'Emotional Impact'].map(area => (
                        <label key={area} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={critiqueAreas.includes(area)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCritiqueAreas([...critiqueAreas, area]);
                              } else {
                                setCritiqueAreas(critiqueAreas.filter(a => a !== area));
                              }
                            }}
                            className="rounded border-violet-300 text-violet-600 focus:ring-violet-500"
                          />
                          <span className="text-violet-900">{area}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={acceptsFollowup}
                        onChange={(e) => setAcceptsFollowup(e.target.checked)}
                        className="rounded border-violet-300 text-violet-600 focus:ring-violet-500"
                      />
                      <span className="text-sm text-violet-900">
                        I am comfortable with follow-up questions from the author
                      </span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-violet-900 mb-2">
                      Is there anything else you'd like the author to know? (Optional)
                    </label>
                    <textarea
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e.target.value)}
                      className="w-full p-3 border border-violet-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      rows={3}
                    />
                  </div>

                  <div className="flex justify-end gap-4">
                    <button
                      onClick={() => setIsApplying(false)}
                      className="px-4 py-2 text-violet-700 hover:text-violet-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApplyAsBetaReader}
                      disabled={!motivation.trim() || !preferredGenres.length || !critiqueAreas.length}
                      className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
                    >
                      Submit Application
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Discussions */}
        <div className="lg:col-span-2">
          <ActivityFeed
            comments={comments}
            onAddComment={addComment}
            onUpdateComment={updateComment}
            onDeleteComment={deleteComment}
            canDeleteComment={canDeleteComment}
            ideaAuthorId={idea.author_id}
          />
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <IdeaSidebar
            characters={characters}
            recentPolls={polls.slice(0, 3)}
            milestones={milestones}
            isAuthor={isAuthor}
            onCreatePoll={() => setIsPollModalOpen(true)}
            onCreateMilestone={() => setIsMilestoneModalOpen(true)}
            onCreateCharacter={() => setIsCharacterModalOpen(true)}
            onToggleCharacterFavorite={toggleCharacterFavorite}
            onVotePoll={votePoll}
            onClosePoll={closePoll}
            onDeletePoll={deletePoll}
            onUpdateMilestone={updateMilestone}
            onDeleteMilestone={deleteMilestone}
          />
        </div>
      </div>

      {/* Modals */}
      <CreatePollModal
        isOpen={isPollModalOpen}
        onClose={() => setIsPollModalOpen(false)}
        onSubmit={createPoll}
      />
      <CreateMilestoneModal
        isOpen={isMilestoneModalOpen}
        onClose={() => setIsMilestoneModalOpen(false)}
        onSubmit={createMilestone}
      />
      <CreateCharacterModal
        isOpen={isCharacterModalOpen}
        onClose={() => setIsCharacterModalOpen(false)}
        onSubmit={createCharacter}
      />
    </div>
  );
}